import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Zap, ArrowRight, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ExitIntentPopup = () => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed or submitted
    if (dismissed || submitted) return;
    // Don't show if already seen this session
    if (sessionStorage.getItem("chill_exit_shown")) return;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 5 && !show && !dismissed && !submitted) {
        setShow(true);
        sessionStorage.setItem("chill_exit_shown", "1");
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [show, dismissed, submitted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || loading) return;
    setLoading(true);

    try {
      await fetch(`${API}/api/leads/capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), source: "exit_intent" }),
      });
      setSubmitted(true);
      if (window.fbq) window.fbq('track', 'Lead', { content_name: 'exit_intent_email' });
    } catch {
      setSubmitted(true); // Don't block UX on error
    }
    setLoading(false);
  };

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
            onClick={handleDismiss}
          />

          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[61] w-[420px] max-w-[90vw] rounded-2xl overflow-hidden"
            style={{ background: "#0c0c0e", border: "1px solid rgba(250,204,21,0.2)", boxShadow: "0 24px 80px rgba(0,0,0,0.6), 0 0 60px rgba(250,204,21,0.1)" }}
            data-testid="exit-intent-popup"
          >
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 text-white/30 hover:text-white transition-colors z-10"
            >
              <X size={18} />
            </button>

            <div className="p-6 pt-8 text-center">
              {!submitted ? (
                <>
                  <div className="w-12 h-12 rounded-full bg-[#FACC15]/10 border border-[#FACC15]/20 flex items-center justify-center mx-auto mb-4">
                    <Zap size={24} className="text-[#FACC15]" />
                  </div>

                  <h3 className="text-xl font-extrabold text-white mb-2">
                    Wait — before you go
                  </h3>
                  <p className="text-white/50 text-sm mb-6 leading-relaxed">
                    Get <span className="text-[#FACC15] font-bold">3 proven referral message templates</span> that actually
                    get responses — free. The same templates our users use to land referrals at Google, Amazon, and Meta.
                  </p>

                  <form onSubmit={handleSubmit} className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email"
                      required
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm outline-none focus:border-[#FACC15]/40 placeholder:text-white/30 transition-colors"
                      data-testid="exit-intent-email"
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-[#FACC15] text-black font-bold text-sm px-5 py-2.5 rounded-lg hover:bg-[#eab308] transition-all flex items-center gap-1"
                      data-testid="exit-intent-submit"
                    >
                      {loading ? <Loader2 size={16} className="animate-spin" /> : <>Send <ArrowRight size={14} /></>}
                    </button>
                  </form>

                  <p className="text-white/20 text-[10px] mt-3">No spam. Unsubscribe anytime.</p>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-full bg-[#22c55e]/10 border border-[#22c55e]/20 flex items-center justify-center mx-auto mb-4">
                    <Zap size={24} className="text-[#22c55e]" />
                  </div>
                  <h3 className="text-xl font-extrabold text-white mb-2">
                    Check your inbox!
                  </h3>
                  <p className="text-white/50 text-sm mb-4">
                    We've sent the templates to your email. While you're here — why not see ChillRefer in action?
                  </p>
                  <a
                    href="/register"
                    className="inline-flex items-center gap-2 bg-[#FACC15] text-black font-bold text-sm px-6 py-2.5 rounded-lg hover:bg-[#eab308] transition-all"
                  >
                    Try ChillRefer Free <ArrowRight size={14} />
                  </a>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
